// Provider provides communcation between components that need to set active chart
import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
//import { HomeAnalyticsChartMode } from './base-chart.component';
import { ImpressionAnalytics, ImpressionService } from '@services/index';
import { numbers as UtilsNumbers, 
  months as UtilsMonths,
  color as UtilsColor ,
  CHART_COLORS } from '@shared/util/chartUtils'

export class AndrenaChartColorConfig
{
  Index: Number;
  LineColor: string;
  FillGradient: CanvasGradient;
}

@Injectable({
  providedIn: 'root'
})
export class BaseChartProvider {
  
  private _selectedHomeChart: BehaviorSubject<number> = new BehaviorSubject(0);
  public selectedHomeChart: Observable<number> = this._selectedHomeChart.asObservable();

  private _activeChartConfig: BehaviorSubject<any> = new BehaviorSubject(null);
  public activeChartConfig: Observable<any> = this._activeChartConfig.asObservable();

  public activeChartModeIndex: number = 0;
  public chartModes: HomeAnalyticsChartMode[];
  public chartConfig: any = {
    type: 'line',
    data: [],
    options: {}
       
  };
  public chartColors: AndrenaChartColorConfig[] = [];
  public LINE_TENSION = 0.4 ;
  public loading: boolean = true;

  constructor(
    private impressionService: ImpressionService,
    //private _ref: ChangeDetectorRef,
  ) { 
    this.setupChartModes();
    this.setupSubscriptions();
  }
  // public methods
  public selectChart(index: number): void {
    this._selectedHomeChart.next(index);
  }

  // private methods
  setupChartModes() {
    this.chartModes = [
      {
        index: 0,
        key: 'Impressions',
        label: 'Impressions',
        isLoaded: false,
        data: null, 
        chartConfig: null,
        observable: this.impressionService.impressionAnalyticsData,
        loader: this.impressionService.fetchImpressionAnalyticsData,
      },
      {
        index: 1,
        key: 'Engagements',
        label: 'Engagements',
        isLoaded: false,
        data: null, 
        chartConfig: null,
        observable: this.impressionService.engagementAnalyticsData,
        loader: this.impressionService.fetchEngagementAnalyticsData,
      },
      {
        index: 2,
        key: 'Backlinks',
        label: 'Backlinks',
        isLoaded: false,
        data: null, 
        chartConfig: null,
        observable: this.impressionService.backlinkAnalyticsData,
        loader: this.impressionService.fetchBacklinkAnalyticsData,
      },
    ];
  }

  setupColors(ctx: CanvasRenderingContext2D): void
  {
    // var canvas = <HTMLCanvasElement>document.getElementById(canvasId);
    // var ctx = canvas.getContext("2d");

    var color1 = 'rgba(103,189,140, 100%)';
    var gradient1 = ctx.createLinearGradient(0, 0, 0, 900);
    gradient1.addColorStop(0, 'rgba(103,189,140,0.8)'); 
    gradient1.addColorStop(0.05, 'rgba(103,189,140,0.2)');     
    gradient1.addColorStop(0.1, 'rgba(103,189,140,0.0)');
   
    var color2 = 'rgba(214,186,131, 100%)';
    var gradient2 = ctx.createLinearGradient(0, 0, 0, 1200);
    gradient2.addColorStop(0, 'rgba(214,186,131,0.8)');
    gradient2.addColorStop(0.1, 'rgba(214,186,131,0.2)');
    gradient2.addColorStop(0.2, 'rgba(214,186,131,0.0)');
    
    var color3 = 'rgba(26,53,221, 100%)';
    var gradient3 = ctx.createLinearGradient(0, 0, 0, 1200);
    gradient3.addColorStop(0, 'rgba(26,53,221,0.2)');
    gradient3.addColorStop(0.25, 'rgba(26,53,221,0.1)');    
    gradient3.addColorStop(0.5, 'rgba(26,53,221,0.0)');
    
    this.chartColors.push({
      Index: 0,
      LineColor: color1,
      FillGradient: gradient1
    })
    this.chartColors.push({
      Index: 1,
      LineColor: color2,
      FillGradient: gradient2
    })
    this.chartColors.push({
      Index: 2,
      LineColor: color3,
      FillGradient: gradient3
    })
  }

  setupSubscriptions(): void {
    //This subscription will listen to selected chart changes and load chart data if needed
    this.selectedHomeChart.subscribe({
      next: (index: number) => {
        if (index === this.activeChartModeIndex) {
          return;
        }
        this.activeChartModeIndex = index;
        if (this.chartModes[index].isLoaded) {
          this.activateChart(index);
        }
        else {
          this.loadChart(index);
        }
      }
    });

  // This subscription will listen to all data observables and generate individual chart configs
  this.chartModes.forEach((chartMode, chartModeIndex) => {
    chartMode.observable.subscribe({
      next: (data: ImpressionAnalytics) => {
        // If data is loaded, configure chart data and config
        if(data && data.Ticks.length > 0) {
          this.loading = false;

          // If data is loaded, configure chart data and config
          let chartData = this.configureChartData(data);
          this.chartModes[chartModeIndex].data = chartData;
          this.chartModes[chartModeIndex].chartConfig = this.configureChartConfig(chartData);
          this.chartModes[chartModeIndex].isLoaded = true;

          // Check current chartModeIndex is also the active chart index, if so, activate chart
          if (chartModeIndex === this._selectedHomeChart.value) {
            this.activateChart(chartModeIndex);
          }
        }
      }
    });
  });
  }

  activateChart(index: number): void {
    this._activeChartConfig.next(this.chartModes[index].chartConfig);
  }

  loadChart(index: number): void {
    // Check if data has been loaded for given chart, call loader if not
    if (!this.chartModes[index].isLoaded)
      this.chartModes[index].loader();
  }

  configureChartConfig(chartData: any): any {
    const chartConfig = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            display: true,
            ticks: {
              display: false
            },
            title: {
              display: false
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            display: true,
            ticks: {
              count: 6
            },
            title: {
              display: false
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        },
        elements: {
          point: {
            backgroundColor: 'rgb(255, 255, 255)',
            borderColor: 'rgb(255, 255, 255)',
            radius: 2,
            pointStyle: 'circle',
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          title: {
            display: false,
            text: 'Chart.js Line Chart'
          },
          filler: {

          }
        }
      },
    };
    return chartConfig;
  }

  configureChartData(data: ImpressionAnalytics): any {
    const DATA_COUNT = data.Ticks.length;
    const chartLabels = UtilsMonths({count: DATA_COUNT});
    const chartData = {
      labels: chartLabels,
      datasets: [
        {
          label: 'Projected',
          //data: UtilsNumbers(IMPRESSIONS_CFG),
          data: data.Ticks.map(x => x.projectedValue),
          borderColor: this.chartColors[0].LineColor,
          pointBackgroundColor: CHART_COLORS.white,
          pointBorderColor: this.chartColors[0].LineColor,
          //backgroundColor: 'rgba(103,189,140, 100%)',
          backgroundColor: this.chartColors[0].FillGradient,
          fill: '+1',
          lineTension: this.LINE_TENSION,
        },
        {
          label: 'Global',
          //data: UtilsNumbers(ENGAGEMENTS_CFG),
          data: data.Ticks.map(x => x.globalValue),
          borderColor: this.chartColors[1].LineColor,
          pointBackgroundColor: CHART_COLORS.white,
          pointBorderColor: this.chartColors[1].LineColor,
          backgroundColor: this.chartColors[1].FillGradient,
          fill: '+1',
          lineTension: this.LINE_TENSION,
        },
        {
          label: 'Domain Value',
          //data: UtilsNumbers(BACKLINKS_CFG),
          data: data.Ticks.map(x => x.domainValue),
          borderColor: this.chartColors[2].LineColor,
          pointBackgroundColor: CHART_COLORS.white,
          pointBorderColor: this.chartColors[2].LineColor,
          //backgroundColor: gradient3,
          backgroundColor: this.chartColors[2].FillGradient,
          fill: 'origin',
          lineTension: this.LINE_TENSION,
        }
      ]
    };
    return chartData;
  }
}

export class HomeAnalyticsChartMode {
  public index: number;
  public key: string;
  public label: string;
  public isLoaded: boolean;
  public observable: Observable<ImpressionAnalytics>;
  public loader: () => void;
  public data: any;
  public chartConfig: any;
}
